import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Store, Plus, Edit2, Trash2, Star, Phone, MapPin,
  Package, TrendingUp, Check, AlertCircle, X, Loader,
  ToggleLeft, ToggleRight, ShoppingBag,
} from 'lucide-react';
import './Boutiques.css';

const AVATAR_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
const avatarColor = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

const EMPTY_FORM = {
  nom: '', adresse: '', telephone: '',
  adminNom: '', adminPrenom: '', adminEmail: '',
  adminTelephone: '', adminPassword: '',
};

export default function Boutiques() {
  const [boutiques,  setBoutiques]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showModal,  setShowModal]  = useState(false);
  const [editMode,   setEditMode]   = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [formData,   setFormData]   = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState({ type: '', text: '' });

  useEffect(() => { fetchBoutiques(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const fetchBoutiques = async () => {
    try {
      const { data } = await axios.get('/api/boutiques');
      setBoutiques(data);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditMode(false); setSelected(null);
    setFormData(EMPTY_FORM); setShowModal(true);
  };

  const openEdit = (b) => {
    setEditMode(true); setSelected(b);
    setFormData({ ...EMPTY_FORM, nom: b.nom, adresse: b.adresse || '', telephone: b.telephone || '' });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setSelected(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) { showToast('error', 'Le nom est requis'); return; }
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`/api/boutiques/${selected._id}`, formData);
        showToast('success', 'Boutique modifiée avec succès');
      } else {
        const { data } = await axios.post('/api/boutiques', formData);
        const base = `Boutique créée${data?.admin?.email ? ` — Admin : ${data.admin.email}` : ''}`;
        showToast('success', data?.admin?.plainPassword ? `${base} | MDP : ${data.admin.plainPassword}` : base);
      }
      closeModal(); fetchBoutiques();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de l\'opération');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Supprimer "${b.nom}" ?`)) return;
    try { await axios.delete(`/api/boutiques/${b._id}`); showToast('success', 'Boutique supprimée'); fetchBoutiques(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  const handleToggle = async (b) => {
    try { await axios.patch(`/api/boutiques/${b._id}/toggle-active`); fetchBoutiques(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  const handlePrincipale = async (b) => {
    if (!window.confirm(`Définir "${b.nom}" comme boutique principale ?`)) return;
    try { await axios.patch(`/api/boutiques/${b._id}/set-principale`); showToast('success', 'Boutique principale mise à jour'); fetchBoutiques(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  const nbActives   = boutiques.filter(b => b.actif).length;
  const principale  = boutiques.find(b => b.principale);

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="bt-page">

      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><Store size={20}/></div>
          <div>
            <h1 className="bt-header-title">Gestion des Boutiques</h1>
            <p className="bt-header-sub">Gérez vos points de vente et leurs administrateurs</p>
          </div>
        </div>
        <button className="bt-cta-btn" onClick={openCreate}>
          <Plus size={14}/> Nouvelle boutique
        </button>
      </div>

      <div className="bt-content">

        {/* KPI row */}
        <div className="bt-kpi-row">
          {[
            { label: 'Total boutiques', value: boutiques.length, color: '#6366f1', bg: '#eef2ff', icon: Store },
            { label: 'Actives',         value: nbActives,         color: '#10b981', bg: '#ecfdf5', icon: Check },
            { label: 'Principale',      value: principale?.nom || '—', color: '#f59e0b', bg: '#fffbeb', icon: Star },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="bt-kpi" style={{ borderTopColor: k.color }}>
                <div className="bt-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={16}/></div>
                <div className="bt-kpi-val">{k.value}</div>
                <div className="bt-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {boutiques.length === 0 ? (
          <div className="bt-empty">
            <div className="bt-empty-icon"><ShoppingBag size={30}/></div>
            <h3>Aucune boutique</h3>
            <p>Créez votre premier point de vente pour commencer</p>
            <button className="bt-cta-btn" onClick={openCreate}><Plus size={13}/> Créer une boutique</button>
          </div>
        ) : (
          <div className="bt-card">
            <table className="bt-table">
              <thead>
                <tr>
                  <th>Boutique</th>
                  <th>Contact</th>
                  <th>Statistiques</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {boutiques.map((b, i) => {
                  const color = avatarColor(i);
                  return (
                    <tr key={b._id} className={!b.actif ? 'bt-row--dim' : ''}>
                      {/* Boutique */}
                      <td>
                        <div className="bt-cell-ent">
                          <div className="bt-av" style={{ background: color + '18', color }}>
                            {b.nom?.charAt(0)?.toUpperCase() || 'B'}
                          </div>
                          <div>
                            <div className="bt-cell-name">
                              {b.nom}
                              {b.principale && (
                                <span className="bt-pill-star"><Star size={9}/> Principale</span>
                              )}
                            </div>
                            {b.adresse && (
                              <div className="bt-cell-sub"><MapPin size={10}/> {b.adresse}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td>
                        {b.telephone
                          ? <div className="bt-cell-sub"><Phone size={11}/> {b.telephone}</div>
                          : <span className="bt-muted">—</span>}
                      </td>

                      {/* Stats */}
                      <td>
                        <div className="bt-stats">
                          <span><Package size={11}/> {b.nbProduits || 0} produits</span>
                          <span><TrendingUp size={11}/> {b.nbVentes || 0} ventes</span>
                        </div>
                      </td>

                      {/* Statut */}
                      <td>
                        <span className={`bt-badge ${b.actif ? 'bt-badge--active' : 'bt-badge--inactive'}`}>
                          {b.actif ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="bt-actions">
                          <button className="bt-action-btn bt-action-btn--edit"
                            onClick={() => openEdit(b)} title="Modifier">
                            <Edit2 size={12}/> Modifier
                          </button>
                          {!b.principale && (
                            <button className="bt-ico-btn" onClick={() => handlePrincipale(b)} title="Définir principale">
                              <Star size={12}/>
                            </button>
                          )}
                          <button className={`bt-ico-btn ${b.actif ? '' : 'bt-ico-btn--on'}`}
                            onClick={() => handleToggle(b)}
                            title={b.actif ? 'Désactiver' : 'Activer'}>
                            {b.actif ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                          </button>
                          <button className="bt-ico-btn bt-ico-btn--del"
                            onClick={() => handleDelete(b)} title="Supprimer">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="bt-overlay" onClick={closeModal}/>
          <div className="bt-modal">
            <div className="bt-modal-head">
              <div>
                <h2 className="bt-modal-title">{editMode ? 'Modifier la boutique' : 'Nouvelle boutique'}</h2>
                <p className="bt-modal-sub">{editMode ? selected?.nom : 'Renseigner les informations du point de vente'}</p>
              </div>
              <button className="bt-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>
            <form onSubmit={handleSubmit} className="bt-modal-body">

              <div className="bt-section-label">Informations boutique</div>
              <div className="bt-field">
                <label>Nom de la boutique *</label>
                <input type="text" value={formData.nom}
                  onChange={e => setFormData({...formData, nom: e.target.value})}
                  placeholder="Ex: Boutique Centre-ville" required autoFocus/>
              </div>
              <div className="bt-form-row">
                <div className="bt-field">
                  <label>Adresse</label>
                  <input type="text" value={formData.adresse}
                    onChange={e => setFormData({...formData, adresse: e.target.value})}
                    placeholder="Ex: Rue 10, Plateau, Dakar"/>
                </div>
                <div className="bt-field">
                  <label>Téléphone</label>
                  <input type="tel" value={formData.telephone}
                    onChange={e => setFormData({...formData, telephone: e.target.value})}
                    placeholder="+221 77 123 45 67"/>
                </div>
              </div>

              {!editMode && (
                <>
                  <div className="bt-divider"/>
                  <div className="bt-section-label">Administrateur de la boutique</div>
                  <p className="bt-section-hint">Laissez vide pour générer automatiquement.</p>
                  <div className="bt-form-row">
                    <div className="bt-field">
                      <label>Prénom</label>
                      <input type="text" value={formData.adminPrenom}
                        onChange={e => setFormData({...formData, adminPrenom: e.target.value})} placeholder="Ex: Fatou"/>
                    </div>
                    <div className="bt-field">
                      <label>Nom</label>
                      <input type="text" value={formData.adminNom}
                        onChange={e => setFormData({...formData, adminNom: e.target.value})} placeholder="Ex: Ndiaye"/>
                    </div>
                  </div>
                  <div className="bt-field">
                    <label>Email de connexion</label>
                    <input type="email" value={formData.adminEmail}
                      onChange={e => setFormData({...formData, adminEmail: e.target.value})} placeholder="admin@boutique.com"/>
                  </div>
                  <div className="bt-form-row">
                    <div className="bt-field">
                      <label>Téléphone admin</label>
                      <input type="tel" value={formData.adminTelephone}
                        onChange={e => setFormData({...formData, adminTelephone: e.target.value})} placeholder="+221 77 …"/>
                    </div>
                    <div className="bt-field">
                      <label>Mot de passe <span className="bt-opt">(optionnel)</span></label>
                      <input type="text" value={formData.adminPassword}
                        onChange={e => setFormData({...formData, adminPassword: e.target.value})} placeholder="Laisser vide pour générer"/>
                    </div>
                  </div>
                </>
              )}

              <div className="bt-alert">
                <AlertCircle size={13}/>
                {editMode ? 'Les modifications seront appliquées immédiatement.' : 'Chaque boutique dispose de son propre stock indépendant.'}
              </div>

              <div className="bt-modal-footer">
                <button type="button" className="bt-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="bt-btn-confirm" disabled={submitting}>
                  {submitting ? <><Loader size={13} className="bt-spin"/> Enregistrement…</> : <><Check size={13}/> {editMode ? 'Enregistrer' : 'Créer la boutique'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

    </div>
  );
}
